import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Alert, CircularProgress, Avatar, LinearProgress, TextField,
  Divider,
} from '@mui/material';
import { Assessment, Download, TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const today = () => new Date().toISOString().slice(0, 10);
const nMonthsAgo = (n: number) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 10); };

function BarMini({ value, max, color = '#1565c0' }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress variant="determinate" value={pct}
        sx={{ flex: 1, height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: color } }} />
      <Typography variant="caption" sx={{ minWidth: 28 }}>{value}</Typography>
    </Box>
  );
}

function NetChip({ net }: { net: number }) {
  const icon = net > 0 ? <TrendingUp fontSize="small" /> : net < 0 ? <TrendingDown fontSize="small" /> : <TrendingFlat fontSize="small" />;
  const color = net > 0 ? '#2e7d32' : net < 0 ? '#b71c1c' : '#78909c';
  return <Chip size="small" icon={icon} label={`${net > 0 ? '+' : ''}${net}`} sx={{ bgcolor: color + '15', color, fontWeight: 700, fontSize: '0.65rem' }} />;
}

interface Snapshot {
  snapshotDate: string; total: number;
  byDept: { label: string; count: number }[];
  byEntity: { label: string; count: number }[];
  byEmploymentType: { label: string; count: number }[];
  byLocation: { label: string; count: number }[];
  byGender: { label: string; count: number }[];
}
interface TrendRow { month: string; headcount: number; joiners: number; leavers: number; net: number }
interface Turnover { leavers: number; startHeadcount: number; endHeadcount: number; avgHeadcount: number; turnoverRate: number }
interface DeptRow { department: string; headcount: string; avgTenureYears: string; fullTime: string; partTime: string; contract: string; female: string; male: string }

export default function ManpowerReport() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [snapDate, setSnapDate]   = useState(today());
  const [fromDate, setFromDate]   = useState(nMonthsAgo(3));
  const [toDate, setToDate]       = useState(today());

  const [snapshot, setSnapshot]   = useState<Snapshot | null>(null);
  const [trend, setTrend]         = useState<TrendRow[]>([]);
  const [joiners, setJoiners]     = useState<any[]>([]);
  const [leavers, setLeavers]     = useState<any[]>([]);
  const [turnover, setTurnover]   = useState<Turnover | null>(null);
  const [deptDetail, setDeptDetail] = useState<DeptRow[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ snapshotDate: snapDate, from: fromDate, to: toDate });
      const data = await apiFetch(`/api/manpower-report/full?${params}`);
      setSnapshot(data.snapshot);
      setTrend(data.trend ?? []);
      setJoiners(data.joiners ?? []);
      setLeavers(data.leavers ?? []);
      setTurnover(data.turnover);
      setDeptDetail(data.deptDetail ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [snapDate, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = (type: 'joiners' | 'leavers') => {
    const url = `${API}/api/manpower-report/export/${type}?from=${fromDate}&to=${toDate}`;
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('Authorization', `Bearer ${localStorage.getItem('token')}`);
    // Use fetch for auth header
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.blob())
      .then(blob => {
        const burl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = burl;
        link.download = `${type}-${fromDate}-${toDate}.csv`;
        link.click();
        URL.revokeObjectURL(burl);
      });
  };

  const TABS = ['Headcount', 'Movement', 'Trend', 'Department Detail'];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#0d47a1', width: 48, height: 48 }}><Assessment /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Manpower Report</Typography>
            <Typography variant="body2" color="text.secondary">Headcount · joiners · leavers · turnover · trends</Typography>
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<Download />} onClick={load}>Refresh</Button>
      </Box>

      {/* Date controls */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="Snapshot Date" type="date" InputLabelProps={{ shrink: true }}
          value={snapDate} onChange={e => setSnapDate(e.target.value)} />
        <Typography variant="body2" color="text.secondary">Movement period:</Typography>
        <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }}
          value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }}
          value={toDate} onChange={e => setToDate(e.target.value)} />
        <Button variant="contained" onClick={load} sx={{ bgcolor: '#0d47a1' }}>Run Report</Button>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPI row */}
      {snapshot && turnover && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Headcount',  value: snapshot.total,              color: '#0d47a1', sub: `as at ${snapDate}` },
            { label: 'Joiners',          value: joiners.length,              color: '#2e7d32', sub: `${fromDate} – ${toDate}` },
            { label: 'Leavers',          value: leavers.length,              color: '#b71c1c', sub: `${fromDate} – ${toDate}` },
            { label: 'Net Change',       value: `${joiners.length - leavers.length >= 0 ? '+' : ''}${joiners.length - leavers.length}`, color: joiners.length >= leavers.length ? '#2e7d32' : '#b71c1c', sub: 'joiners − leavers' },
            { label: 'Turnover Rate',    value: `${turnover.turnoverRate}%`, color: turnover.turnoverRate > 15 ? '#b71c1c' : '#2e7d32', sub: 'annualised' },
            { label: 'Avg Headcount',    value: turnover.avgHeadcount,       color: '#546e7a', sub: 'over period' },
          ].map(k => (
            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={k.label}>
              <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${k.color}` }}>
                <Typography variant="h5" fontWeight={700} color={k.color}>{k.value}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{k.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{k.sub}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            {TABS.map((t, i) => <Tab key={i} label={t} />)}
          </Tabs>

          {/* ── Headcount ── */}
          {tab === 0 && snapshot && (
            <Grid container spacing={3}>
              {[
                { title: 'By Department',       data: snapshot.byDept,           color: '#1565c0' },
                { title: 'By Entity',            data: snapshot.byEntity,         color: '#6a1b9a' },
                { title: 'By Employment Type',   data: snapshot.byEmploymentType, color: '#2e7d32' },
                { title: 'By Location',          data: snapshot.byLocation,       color: '#e65100' },
                { title: 'By Gender',            data: snapshot.byGender,         color: '#880e4f' },
              ].map(panel => (
                <Grid size={{ xs: 12, md: 6 }} key={panel.title}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>{panel.title}</Typography>
                    {panel.data.map(row => (
                      <Box key={row.label} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="body2">{row.label}</Typography>
                          <Typography variant="body2" fontWeight={600}>{Math.round((row.count / snapshot.total) * 100)}%</Typography>
                        </Box>
                        <BarMini value={row.count} max={snapshot.total} color={panel.color} />
                      </Box>
                    ))}
                    {panel.data.length === 0 && <Typography variant="body2" color="text.secondary">No data</Typography>}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          {/* ── Movement ── */}
          {tab === 1 && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Joiners ({joiners.length})
                    </Typography>
                    <Button size="small" startIcon={<Download />} onClick={() => exportCsv('joiners')}>CSV</Button>
                  </Box>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Department</TableCell>
                          <TableCell>Position</TableCell>
                          <TableCell>Start Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {joiners.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>No joiners in period</TableCell></TableRow>}
                        {joiners.map((j, i) => (
                          <TableRow key={i} hover>
                            <TableCell><Typography variant="body2" fontWeight={600}>{j.name}</Typography></TableCell>
                            <TableCell><Typography variant="caption">{j.department}</Typography></TableCell>
                            <TableCell><Typography variant="caption">{j.position}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{j.startDate}</Typography></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Leavers ({leavers.length})
                    </Typography>
                    <Button size="small" startIcon={<Download />} onClick={() => exportCsv('leavers')}>CSV</Button>
                  </Box>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Department</TableCell>
                          <TableCell>End Date</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leavers.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>No leavers in period</TableCell></TableRow>}
                        {leavers.map((l, i) => (
                          <TableRow key={i} hover>
                            <TableCell><Typography variant="body2" fontWeight={600}>{l.name}</Typography></TableCell>
                            <TableCell><Typography variant="caption">{l.department}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{l.endDate}</Typography></TableCell>
                            <TableCell>
                              <Chip size="small" label={l.employmentStatus}
                                sx={{ fontSize: '0.6rem',
                                  bgcolor: l.employmentStatus === 'resigned' ? '#fff3e0' : '#ffebee',
                                  color:   l.employmentStatus === 'resigned' ? '#e65100' : '#b71c1c' }} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* Turnover summary */}
              {turnover && (
                <Grid size={{ xs: 12 }}>
                  <Paper sx={{ p: 2, display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight={700} color={turnover.turnoverRate > 15 ? '#b71c1c' : '#2e7d32'}>
                        {turnover.turnoverRate}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Turnover Rate</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    {[
                      { label: 'Opening HC', value: turnover.startHeadcount },
                      { label: 'Closing HC',  value: turnover.endHeadcount },
                      { label: 'Avg HC',      value: turnover.avgHeadcount },
                      { label: 'Leavers',     value: turnover.leavers },
                    ].map(s => (
                      <Box key={s.label} sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                      </Box>
                    ))}
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: 'right' }}>
                      Formula: Leavers ÷ Avg Headcount × 100
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}

          {/* ── Trend ── */}
          {tab === 2 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>12-Month Headcount Trend</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Headcount</TableCell>
                      <TableCell align="right">Joiners</TableCell>
                      <TableCell align="right">Leavers</TableCell>
                      <TableCell align="center">Net</TableCell>
                      <TableCell>Bar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const maxHc = Math.max(...trend.map(t => t.headcount), 1);
                      return trend.map(t => (
                        <TableRow key={t.month} hover>
                          <TableCell><Typography variant="body2" fontWeight={600}>{t.month}</Typography></TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{t.headcount}</TableCell>
                          <TableCell align="right" sx={{ color: '#2e7d32' }}>+{t.joiners}</TableCell>
                          <TableCell align="right" sx={{ color: t.leavers > 0 ? '#b71c1c' : 'text.secondary' }}>
                            {t.leavers > 0 ? `-${t.leavers}` : '0'}
                          </TableCell>
                          <TableCell align="center"><NetChip net={t.net} /></TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <LinearProgress variant="determinate" value={Math.round((t.headcount / maxHc) * 100)}
                              sx={{ height: 10, borderRadius: 5, '& .MuiLinearProgress-bar': { bgcolor: '#0d47a1' } }} />
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* ── Department Detail ── */}
          {tab === 3 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">HC</TableCell>
                    <TableCell align="right">Avg Tenure (yrs)</TableCell>
                    <TableCell align="right">Full-time</TableCell>
                    <TableCell align="right">Part-time</TableCell>
                    <TableCell align="right">Contract</TableCell>
                    <TableCell align="right">Female</TableCell>
                    <TableCell align="right">Male</TableCell>
                    <TableCell>HC Bar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const maxHc = Math.max(...deptDetail.map(d => parseInt(d.headcount)), 1);
                    return deptDetail.map(d => (
                      <TableRow key={d.department} hover>
                        <TableCell><Typography variant="body2" fontWeight={600}>{d.department || 'Unknown'}</Typography></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{d.headcount}</TableCell>
                        <TableCell align="right">{d.avgTenureYears ?? '—'}</TableCell>
                        <TableCell align="right">{d.fullTime}</TableCell>
                        <TableCell align="right">{d.partTime}</TableCell>
                        <TableCell align="right">{d.contract}</TableCell>
                        <TableCell align="right">{d.female}</TableCell>
                        <TableCell align="right">{d.male}</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <BarMini value={parseInt(d.headcount)} max={maxHc} color="#0d47a1" />
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
